import i18n from "../i18n";
import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Modal,
  Paper,
  Stack,
  Tabs,
  Text,
  Title,
} from "@mantine/core";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthUser } from "react-auth-kit";
import moment from "moment";
import { PageList, ENUM_FORM_DISPLAY } from "../components/pageList";
import { useServerApi } from "../hooks/userServerApi";
import {
  requestNotificationInboxRefresh,
} from "../hooks/useNotificationInbox";
import {
  NOTIFICATION_ENTITIES,
  NOTIFICATION_EVENTS,
  NOTIFICATION_PRIORITIES,
  entityLabel,
  eventLabel,
} from "../data/notificationEntities";
import { useTabNavigate } from "../hooks/useTabNavigate";
import { openEntityEditTab } from "../utils/openEntityEditTab";
import {
  buildEntityEditHref,
  isEntityRecordLink,
  rewriteEntityLinks,
} from "../utils/entityEditHref";

const apiEntity = "userNotification";

const formSchema = {
  title: "Notification",
  apiEntity,
  display: { mode: ENUM_FORM_DISPLAY.MODAL, size: "sm" },
  initialValues: {},
  layout: { fields: [] },
};

const eventBadgeColor = (event) => {
  const normalized = event === "FIELD_CHANGE" ? "UPDATE" : event;
  if (normalized === "CREATE") return "teal";
  if (normalized === "UPDATE") return "blue";
  if (normalized === "DELETE") return "red";
  return "gray";
};

const TestBadge = () => (
  <Badge size="xs" color="orange" variant="outline" sx={{ flexShrink: 0 }}>{i18n.t("Test")}</Badge>
);

const oneLineText = (content, { weight, dimmed, title } = {}) => (
  <Text
    size="sm"
    weight={weight}
    color={dimmed ? "dimmed" : undefined}
    lineClamp={1}
    title={title ?? (typeof content === "string" ? content : undefined)}
    sx={{ lineHeight: 1.45 }}
  >
    {content}
  </Text>
);

const hasHtml = (value = "") => /<[a-z][\s\S]*>/i.test(value);

const buildTableSchema = ({ onToggleRead, onRowDoubleClick, onOpenEntity, listName }) => ({
  name: listName,
  showActionColumn: false,
  onRowDoubleClick,
  advanceFilters: [
    {
      field: "event",
      label: "Type",
      type: "select",
      options: NOTIFICATION_EVENTS,
    },
    {
      field: "entityType",
      label: "Entity Type",
      type: "select",
      options: NOTIFICATION_ENTITIES,
    },
    {
      field: "priority",
      label: "Priority",
      type: "select",
      options: NOTIFICATION_PRIORITIES,
    },
    {
      field: "createdAt",
      label: "Created",
      type: "dateRange",
      props: {
        label: "Created",
        placeholder: "Select date range",
      },
    },
  ],
  columns: [
    {
      field: "createdAt",
      headerName: "Date Time",
      width: 148,
      sortable: true,
      cellRender: (col, data) => (
        <Text size="sm" sx={{ whiteSpace: "nowrap", lineHeight: 1.45 }}>
          {data?.createdAt ? moment(data.createdAt).format("YYYY-MM-DD HH:mm") : "—"}
        </Text>
      ),
    },
    {
      field: "event",
      headerName: "Type",
      width: 118,
      sortable: true,
      filterable: true,
      filter: {
        apiEntity,
        field: "event",
        label: "Type",
        width: 260,
        height: 220,
      },
      cellRender: (col, data) => {
        const event = data?.event ?? data?.log?.event;
        if (!event && !data?.isTest) return "";
        return (
          <Group spacing={6} noWrap sx={{ flexWrap: "nowrap" }}>
            {event ? (
              <Badge size="sm" color={eventBadgeColor(event)} sx={{ flexShrink: 0 }}>
                {eventLabel(event)}
              </Badge>
            ) : null}
            {data?.isTest ? <TestBadge /> : null}
          </Group>
        );
      },
    },
    {
      field: "entityType",
      headerName: "Entity Type",
      width: 112,
      sortable: true,
      filterable: true,
      filter: {
        apiEntity,
        field: "entityType",
        label: "Entity Type",
        width: 260,
        height: 220,
      },
      cellRender: (col, data) =>
        oneLineText(entityLabel(data?.entityType ?? data?.category)),
    },
    {
      field: "entityCode",
      headerName: "Entity",
      width: 220,
      sortable: true,
      cellRender: (col, data) => {
        const code = data?.entityCode;
        const name = data?.entityName;
        const label =
          code && name ? `[${code}] ${name}` : code ? `[${code}]` : name || "—";
        if (data?.entityType && data?.entityId && onOpenEntity) {
          return (
            <Text
              size="sm"
              weight={500}
              lineClamp={1}
              title={label}
              sx={(theme) => ({
                color: theme.colors.blue[6],
                cursor: "pointer",
                lineHeight: 1.45,
              })}
              onClick={(e) => {
                e.stopPropagation();
                onOpenEntity(data);
              }}
            >
              {label}
            </Text>
          );
        }
        return oneLineText(label, { dimmed: !code && !name, title: label });
      },
    },
    {
      field: "title",
      headerName: "Subject",
      flex: 1,
      minWidth: 240,
      sortable: true,
      cellRender: (col, data) =>
        oneLineText(data?.title ?? "—", {
          weight: data?.readAt ? 400 : 600,
          title: data?.title,
        }),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 88,
      isAction: true,
      cellRender: (col, data) => (
        <Button
          size="xs"
          variant="subtle"
          compact
          onClick={(e) => {
            e.stopPropagation();
            onToggleRead(data);
          }}
        >
          {data?.readAt ? "Unread" : "Read"}
        </Button>
      ),
    },
  ],
});

const formatLegacyPlainBody = (value = "") => {
  const text = String(value ?? "").trim();
  if (!text || text.includes("\n")) return text;
  return text
    .replace(/,\s*(The\s+)/i, ",\n\n$1")
    .replace(/\.\s*(Please\s+)/i, ".\n$1")
    .replace(/\s*(Best Regards,?)/i, "\n\n$1");
};

const NotificationBody = ({
  html,
  text,
  entityType,
  entityCode,
  entityUrl,
  onEntityLinkClick,
}) => {
  const content = html || text || "";
  if (!content) return <Text color="dimmed">—</Text>;

  const rewritten = rewriteEntityLinks(content, {
    entityName: entityType,
    entityCode,
    entityUrl,
  });

  const handleClick = (event) => {
    const anchor = event.target?.closest?.("a");
    if (!anchor || !onEntityLinkClick) return;
    const href = anchor.getAttribute("href") || "";
    const label = (anchor.textContent || "").trim();
    if (
      !isEntityRecordLink({
        href,
        text: label,
        entityCode,
        entityName: entityType,
      })
    ) {
      return;
    }
    event.preventDefault();
    onEntityLinkClick();
  };

  if (hasHtml(rewritten)) {
    return (
      <Box
        onClick={handleClick}
        sx={(theme) => ({
          color: "inherit",
          fontSize: theme.fontSizes.sm,
          lineHeight: 1.6,
          wordBreak: "break-word",
          "& p": { margin: "0 0 0.85em" },
          "& p:last-of-type": { marginBottom: 0 },
          "& strong, & b": { fontWeight: 700 },
          "& em, & i": { fontStyle: "italic" },
          "& u": { textDecoration: "underline" },
          "& s": { textDecoration: "line-through" },
          "& ul, & ol": {
            marginTop: 0,
            marginBottom: theme.spacing.sm,
            paddingLeft: theme.spacing.lg,
          },
          "& li": { marginBottom: 4 },
          "& a": { color: theme.colors.blue[4] },
        })}
        dangerouslySetInnerHTML={{ __html: rewritten }}
      />
    );
  }

  return (
    <Text
      size="sm"
      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.6 }}
    >
      {formatLegacyPlainBody(rewritten)}
    </Text>
  );
};

const NotificationDetailModal = ({
  notification,
  onClose,
  onToggleRead,
  onOpenEntity,
  onAcknowledge,
}) => {
  if (!notification) return null;

  const createdAt = notification.createdAt
    ? moment(notification.createdAt).format("YYYY-MM-DD HH:mm:ss")
    : "—";
  const entityUrl = buildEntityEditHref(
    notification.entityType,
    notification.entityId
  );

  return (
    <Modal
      opened={!!notification}
      onClose={onClose}
      title={
        <Group spacing="xs">
          <Text weight={600}>{i18n.t("Notification Details")}</Text>
          {notification.isTest ? <TestBadge /> : null}
        </Group>
      }
      size="xl"
    >
      <Stack spacing="md">
        <Paper withBorder p="md" radius="md">
          <Stack spacing="sm">
            <Group position="apart" align="flex-start" spacing="md" noWrap>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Text size="sm" sx={{ wordBreak: "break-word" }}>
                  {notification.title ?? "—"}
                </Text>
              </Box>
              <Stack spacing={2} align="flex-end" sx={{ flexShrink: 0 }}>
                <Text size="xs" color="dimmed">
                  {notification.code ?? "—"}
                </Text>
                <Text size="xs" color="dimmed">
                  {createdAt}
                </Text>
              </Stack>
            </Group>
            <Divider />
            <Box>
              <NotificationBody
                html={notification.bodyHtml ?? notification.log?.body}
                text={notification.body}
                entityType={notification.entityType}
                entityCode={notification.entityCode}
                entityUrl={entityUrl}
                onEntityLinkClick={() => onOpenEntity?.(notification)}
              />
            </Box>
          </Stack>
        </Paper>
      </Stack>

      <Group position="right" mt="lg">
        {notification.acknowledgedAt ? (
          <Text size="sm" color="dimmed">
            Acknowledged {moment(notification.acknowledgedAt).format("YYYY-MM-DD HH:mm")}
          </Text>
        ) : null}
        <Button variant="default" onClick={onClose}>{i18n.t("Close")}</Button>
        {notification.requireAcknowledgement && !notification.acknowledgedAt ? (
          <Button
            color="teal"
            onClick={() => onAcknowledge?.(notification)}
          >
            Acknowledge
          </Button>
        ) : null}
        <Button
          onClick={() => {
            onToggleRead(notification);
            onClose();
          }}
        >
          {notification.readAt ? "Mark unread" : "Mark read"}
        </Button>
      </Group>
    </Modal>
  );
};

const NotificationList = ({
  preQuery,
  listName,
  onToggleRead,
  onRowDoubleClick,
  onOpenEntity,
}) => {
  const tableSchema = useMemo(
    () =>
      buildTableSchema({
        listName,
        onToggleRead,
        onRowDoubleClick,
        onOpenEntity,
      }),
    [listName, onToggleRead, onRowDoubleClick, onOpenEntity]
  );

  return (
    <PageList
      apiEntity={apiEntity}
      title="Notifications"
      formSchema={formSchema}
      tableSchema={tableSchema}
      showAddButton={false}
      hideActionButton={true}
      columnFilterLayout="toolbar"
      searchByTextField={true}
      defaultPageSize={20}
      initSort={{ by: "createdAt", order: "desc" }}
      preQuery={preQuery}
    />
  );
};

const NotificationCenter = () => {
  const [api] = useServerApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const navigate = useNavigate();
  const location = useLocation();
  const tabNav = useTabNavigate();
  const auth = useAuthUser();
  const session = auth();
  const [tab, setTab] = useState("All");
  const [detail, setDetail] = useState(null);

  const handleRowDoubleClick = useCallback(({ data }) => {
    if (!data) return;
    setDetail(data);
  }, []);

  const handleOpenEntity = useCallback(
    (data) => {
      if (!data?.entityType || !data?.entityId) return;
      openEntityEditTab(tabNav, {
        entity: data.entityType,
        id: data.entityId,
        label: data.entityCode ?? entityLabel(data.entityType),
        userRole: session?.userRole,
        currentPathname: location.pathname,
        pathname:
          data.entityType === "debitNote" ? "/m/gi/debitNote" : undefined,
      });
    },
    [tabNav, session?.userRole, location.pathname]
  );

  const handleMarkAllRead = async () => {
    await apiRef.current.Notification.markAllRead();
    requestNotificationInboxRefresh();
  };

  const handleToggleRead = useCallback(async (data) => {
    if (!data?._id) return;
    if (data.readAt) {
      await apiRef.current.Notification.markUnread(data._id);
    } else {
      await apiRef.current.Notification.markRead(data._id);
    }
    requestNotificationInboxRefresh();
  }, []);

  const handleAcknowledge = useCallback(async (data) => {
    if (!data?._id) return;
    const result = await apiRef.current.Notification.acknowledge(data._id);
    const saved = result?.data;
    if (saved?.acknowledgedAt) {
      setDetail((current) =>
        current && current._id === data._id ? { ...current, ...saved } : current
      );
    }
    requestNotificationInboxRefresh();
  }, []);

  return (
    <Stack p="md" spacing="md">
      <Group position="apart">
        <Title order={3}>{i18n.t("Notification Center")}</Title>
        <Group>
          <Button
            variant="subtle"
            onClick={() => navigate("/settings/notificationPreferences")}
          >
            Preferences
          </Button>
          <Button variant="light" onClick={handleMarkAllRead}>
            Mark all read
          </Button>
        </Group>
      </Group>

      <Tabs value={tab} onTabChange={setTab}>
        <Tabs.List px="xs">
          <Tabs.Tab value="Unread">{i18n.t("Unread")}</Tabs.Tab>
          <Tabs.Tab value="All">All</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="Unread" pt="md">
          {tab === "Unread" && (
            <NotificationList
              listName="unread"
              preQuery={{ readAt: null }}
              onToggleRead={handleToggleRead}
              onRowDoubleClick={handleRowDoubleClick}
              onOpenEntity={handleOpenEntity}
            />
          )}
        </Tabs.Panel>

        <Tabs.Panel value="All" pt="md">
          {tab === "All" && (
            <NotificationList
              listName="all"
              preQuery={{}}
              onToggleRead={handleToggleRead}
              onRowDoubleClick={handleRowDoubleClick}
              onOpenEntity={handleOpenEntity}
            />
          )}
        </Tabs.Panel>
      </Tabs>

      <NotificationDetailModal
        notification={detail}
        onClose={() => setDetail(null)}
        onToggleRead={handleToggleRead}
        onAcknowledge={handleAcknowledge}
        onOpenEntity={(data) => {
          handleOpenEntity(data);
          setDetail(null);
        }}
      />
    </Stack>
  );
};

export default NotificationCenter;
